import { Component } from '@theme/component';

/**
 * @typedef {Object} EmpCelebridadesRefs
 * @property {HTMLElement} track - The horizontal scroller holding the cards
 * @property {HTMLButtonElement} [prev] - Previous arrow
 * @property {HTMLButtonElement} [next] - Next arrow
 */

/**
 * The celebrity carousel on the home page (sections/emp-celebridades.liquid).
 *
 * Ported from Odorato's inline script in `fragancias-de-celebridades.liquid`.
 * Native scroll-snap does the scrolling; this component only adds:
 * - an infinite loop: the card set is cloned once before and once after, and
 *   the scroll position is teleported by exactly one set width whenever it
 *   drifts far into the clones, so the jump is invisible;
 * - arrows that move exactly one card per click, adding up rapid clicks;
 * - an optional continuous drift (marquee-like) that pauses on hover, focus
 *   and touch, and never runs with reduced motion.
 *
 * @extends {Component<EmpCelebridadesRefs>}
 */
class EmpCelebridades extends Component {
  requiredRefs = ['track'];

  /** @type {HTMLElement[]} */
  #originals = [];
  #setWidth = 0;
  #stepWidth = 0;
  #jumping = false;
  /** @type {number | null} */
  #pendingTarget = null;
  #looping = false;
  #paused = false;
  #ticking = false;
  /** @type {number | undefined} */
  #frame;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pauseTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settleTimer;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { track, prev, next } = this.refs;
    this.#originals = Array.from(track.querySelectorAll('.emp-cel-card'));
    if (!this.#originals.length) return;

    const signal = this.#abort.signal;
    this.#looping = this.#originals.length >= 2;

    if (this.#looping) {
      this.#buildClones();
      // Measure once the clones are laid out.
      requestAnimationFrame(() => requestAnimationFrame(() => this.#measureAndCenter()));

      this.#resizeObserver = new ResizeObserver(() => this.#remeasureOnResize());
      this.#resizeObserver.observe(track);

      track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
      if ('onscrollend' in track) {
        track.addEventListener('scrollend', this.#settle, { signal });
      } else {
        track.addEventListener(
          'scroll',
          () => {
            clearTimeout(this.#settleTimer);
            this.#settleTimer = setTimeout(this.#settle, 150);
          },
          { passive: true, signal }
        );
      }
    }

    prev?.addEventListener('click', () => this.#step(-1), { signal });
    next?.addEventListener('click', () => this.#step(1), { signal });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.dataset.autoplay === 'true' && this.#looping && !reduceMotion) {
      this.#startDrift(signal);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#abort = new AbortController();
    this.#resizeObserver?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    clearTimeout(this.#pauseTimer);
    clearTimeout(this.#settleTimer);
    // The theme editor re-renders the section; drop the clones so a reconnect
    // starts from the original cards only.
    this.refs.track?.querySelectorAll('.emp-cel-card--clone').forEach((clone) => clone.remove());
  }

  #buildClones() {
    const { track } = this.refs;
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    for (const card of this.#originals) {
      for (const fragment of [before, after]) {
        const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
        clone.classList.add('emp-cel-card--clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        fragment.appendChild(clone);
      }
    }

    track.prepend(before);
    track.append(after);
  }

  /** One "set" is the distance from the first cloned card to the first real one. */
  #measure() {
    const { track } = this.refs;
    const first = /** @type {HTMLElement} */ (track.firstElementChild);
    const original = this.#originals[0];
    if (!first || !original) return;
    this.#setWidth = original.offsetLeft - first.offsetLeft;
    this.#stepWidth = this.#setWidth / this.#originals.length;
  }

  /**
   * Scrolls without the smooth behavior set in CSS.
   * @param {number} left
   */
  #instantScrollTo(left) {
    const { track } = this.refs;
    const previous = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = left;
    track.style.scrollBehavior = previous;
  }

  #measureAndCenter() {
    this.#measure();
    this.#pendingTarget = null;
    if (this.#setWidth) this.#instantScrollTo(this.#setWidth);
  }

  #remeasureOnResize() {
    const previous = this.#setWidth;
    this.#measure();
    if (this.#setWidth !== previous) {
      this.#pendingTarget = null;
      this.#instantScrollTo(this.#setWidth);
    }
  }

  /** @param {number} delta */
  #jumpBy(delta) {
    this.#jumping = true;
    this.#instantScrollTo(this.refs.track.scrollLeft + delta);
    requestAnimationFrame(() => {
      this.#jumping = false;
    });
  }

  /**
   * Teleports only deep inside the clones, half a set away from any resting
   * position, so no gesture crosses a boundary on its first pixel.
   */
  #handleLoop() {
    if (!this.#looping || this.#jumping || !this.#setWidth) return;
    const x = this.refs.track.scrollLeft;
    if (x < this.#setWidth * 0.5) this.#jumpBy(this.#setWidth);
    else if (x >= this.#setWidth * 2.5) this.#jumpBy(-this.#setWidth);
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#handleLoop();
      this.#ticking = false;
    });
  };

  /** At rest, return to the middle set so there is room on both sides. */
  #settle = () => {
    this.#pendingTarget = null;
    if (!this.#looping || !this.#setWidth) return;
    const x = this.refs.track.scrollLeft;
    if (x < this.#setWidth) this.#jumpBy(this.#setWidth);
    else if (x >= this.#setWidth * 2) this.#jumpBy(-this.#setWidth);
  };

  /** @param {number} direction - 1 for next, -1 for previous */
  #step(direction) {
    const { track } = this.refs;
    const first = this.#originals[0];
    if (!first) return;
    const step = this.#stepWidth || first.offsetWidth + (parseFloat(getComputedStyle(track).columnGap) || 0);
    const base = this.#pendingTarget ?? Math.round(track.scrollLeft / step) * step;
    let target = base + direction * step;

    if (this.#looping && this.#setWidth && (target <= this.#setWidth * 0.5 || target >= this.#setWidth * 2.5)) {
      const shift = direction < 0 ? this.#setWidth : -this.#setWidth;
      this.#instantScrollTo(track.scrollLeft + shift);
      target += shift;
    }

    this.#pendingTarget = target;
    track.scrollTo({ left: target, behavior: 'smooth' });
  }

  /** @param {AbortSignal} signal */
  #startDrift(signal) {
    const { track } = this.refs;
    const speed = parseFloat(this.dataset.autoplaySpeed ?? '') || 4;
    const pxPerFrame = Math.max(0.25, speed * 0.25);

    const driftOn = () => {
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
    };
    const driftOff = () => {
      track.style.scrollBehavior = '';
      track.style.scrollSnapType = '';
    };
    const pause = () => {
      this.#paused = true;
      driftOff();
    };
    const resume = () => {
      this.#paused = false;
      driftOn();
    };
    const pauseBriefly = () => {
      pause();
      clearTimeout(this.#pauseTimer);
      this.#pauseTimer = setTimeout(resume, 1200);
    };
    const drift = () => {
      if (!this.#paused) {
        track.scrollLeft += pxPerFrame;
        this.#handleLoop();
      }
      this.#frame = requestAnimationFrame(drift);
    };

    this.addEventListener('mouseenter', pause, { signal });
    this.addEventListener('mouseleave', resume, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', resume, { signal });
    track.addEventListener('touchstart', pauseBriefly, { passive: true, signal });
    track.addEventListener('wheel', pauseBriefly, { passive: true, signal });

    // Start after the initial centering.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        driftOn();
        drift();
      })
    );
  }
}

if (!customElements.get('emp-celebridades')) {
  customElements.define('emp-celebridades', EmpCelebridades);
}
